import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { getLegacyTransition, getLegacyUpgradeAction, isLegacyAccessEnded } from './legacyTransition';

type JsonRecord = Record<string, unknown>;

const UI_ONLY_FEATURE_IDS = {
    deliveryRss: 'delivery_rss',
    canPrivateProjects: 'can_private_projects',
} as const;

export interface UiEntitlementSnapshot {
    features?: JsonRecord;
    derived?: JsonRecord;
    billing_preview?: JsonRecord | null;
    legacy_transition?: JsonRecord | null;
}

export type RssUpgradeAction =
    | {
        path: null;
        label: MessageDescriptor;
        kind: 'available';
    }
    | {
        path: '/early-user-offer' | '/pricing';
        label: MessageDescriptor;
        kind: 'legacy_offer' | 'plans' | 'trial';
    };

function parseBooleanValue(value: unknown, defaultValue = false): boolean {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'number') {
        return value !== 0;
    }

    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (['true', 't', '1', 'yes', 'y', 'on'].includes(normalized)) {
            return true;
        }
        if (['false', 'f', '0', 'no', 'n', 'off'].includes(normalized)) {
            return false;
        }
    }

    return defaultValue;
}

function getEntitlementSource(snapshot: UiEntitlementSnapshot | null | undefined): string {
    const source = typeof snapshot?.derived?.entitlement_source === 'string'
        ? snapshot.derived.entitlement_source
        : '';

    return source || 'free';
}

function isLegacyEntitlementGrace(snapshot: UiEntitlementSnapshot | null | undefined): boolean {
    return parseBooleanValue(snapshot?.derived?.legacy_entitlement_grace, false)
        || getEntitlementSource(snapshot) === 'legacy_grace';
}

function requiresBillingPreviewCheckout(snapshot: UiEntitlementSnapshot | null | undefined): boolean {
    return parseBooleanValue(snapshot?.billing_preview?.requires_checkout, false);
}

export function shouldPresentLegacyGraceAccess(
    snapshot: UiEntitlementSnapshot | null | undefined
): boolean {
    return !isLegacyAccessEnded(snapshot)
        && getEntitlementSource(snapshot) !== 'subscription'
        && isLegacyEntitlementGrace(snapshot);
}

export function shouldPromptBillingPreviewCheckout(snapshot: UiEntitlementSnapshot | null | undefined): boolean {
    const entitlementSource = getEntitlementSource(snapshot);

    return requiresBillingPreviewCheckout(snapshot)
        && !isLegacyAccessEnded(snapshot)
        && !isLegacyEntitlementGrace(snapshot)
        && entitlementSource !== 'legacy_grace'
        && entitlementSource !== 'subscription'
        && entitlementSource !== 'worker_fallback';
}

function canUseUiOnlyFeature(
    snapshot: UiEntitlementSnapshot | null | undefined,
    featureId: string
): boolean {
    if (!snapshot) {
        return true;
    }

    if (isLegacyEntitlementGrace(snapshot)) {
        return true;
    }

    if (getEntitlementSource(snapshot) === 'worker_fallback') {
        return true;
    }

    if (!snapshot.features || !(featureId in snapshot.features)) {
        return true;
    }

    return parseBooleanValue(snapshot.features[featureId], false);
}

export function canAccessRssDelivery(snapshot: UiEntitlementSnapshot | null | undefined): boolean {
    if (getLegacyTransition(snapshot).rss_grandfathered) {
        return true;
    }

    return canUseUiOnlyFeature(snapshot, UI_ONLY_FEATURE_IDS.deliveryRss);
}

export function shouldBlockRssDelivery(snapshot: UiEntitlementSnapshot | null | undefined): boolean {
    return Boolean(snapshot) && !canAccessRssDelivery(snapshot);
}

export function canUsePrivateProjects(snapshot: UiEntitlementSnapshot | null | undefined): boolean {
    return canUseUiOnlyFeature(snapshot, UI_ONLY_FEATURE_IDS.canPrivateProjects);
}

export function shouldBlockPrivateProjects(snapshot: UiEntitlementSnapshot | null | undefined): boolean {
    return Boolean(snapshot) && !canUsePrivateProjects(snapshot);
}

export function buildRssUpgradeMessage(snapshot: UiEntitlementSnapshot | null | undefined): MessageDescriptor {
    if (getLegacyTransition(snapshot).is_former_legacy) {
        return msg({
            id: 'entitlements.rss.legacyAvailable',
            message: 'RSS/Atom remains available on your account. Please try again.',
        });
    }

    if (shouldPromptBillingPreviewCheckout(snapshot)) {
        return msg({
            id: 'entitlements.rss.startTrial',
            message: 'Start a 14-day free trial to copy RSS/Atom feeds for this topic. No obligation to buy.',
        });
    }

    return msg({
        id: 'entitlements.rss.paidPlans',
        message: 'RSS/Atom feeds are included with paid plans. View plans when you want to read Paperzilla in your feed reader.',
    });
}

export function getRssUpgradeAction(
    snapshot: UiEntitlementSnapshot | null | undefined
): RssUpgradeAction {
    if (getLegacyTransition(snapshot).is_former_legacy) {
        return {
            path: null,
            label: msg({ id: 'entitlements.rss.remainsAvailable', message: 'RSS/Atom remains available' }),
            kind: 'available',
        };
    }

    if (shouldPromptBillingPreviewCheckout(snapshot)) {
        return {
            path: '/pricing',
            label: msg({ id: 'entitlements.startTrial', message: 'Start a 14-day free trial' }),
            kind: 'trial',
        };
    }

    return {
        path: '/pricing',
        label: msg({ id: 'billing.viewPlans', message: 'View plans' }),
        kind: 'plans',
    };
}

export function buildPrivateProjectUpgradeMessage(snapshot: UiEntitlementSnapshot | null | undefined): MessageDescriptor {
    if (isLegacyAccessEnded(snapshot)) {
        const upgradeAction = getLegacyUpgradeAction(snapshot);
        return upgradeAction.hasOffer
            ? msg({
                id: 'entitlements.privateProjects.earlyUserOffer',
                message: 'Your account is now on the Free plan. View your early-user offer to use private topics again.',
            })
            : msg({
                id: 'entitlements.privateProjects.viewPlans',
                message: 'Your account is now on the Free plan. View plans to use private topics again.',
            });
    }

    if (shouldPromptBillingPreviewCheckout(snapshot)) {
        return msg({
            id: 'entitlements.privateProjects.startTrial',
            message: 'Start a 14-day free trial to make this topic private. No obligation to buy.',
        });
    }

    return msg({
        id: 'entitlements.privateProjects.paidPlans',
        message: 'Private topics are included with Plus and Pro. View plans when you need a private workspace.',
    });
}
